/** The view rows: today, scanner, board, detail, accounts, mirroring `ssi.api.views` (ruling 0.3). */
import { PROBABILITY_SCALE } from "./constants.ts";
import {
  ContractViolation,
  bool,
  decimal,
  exact,
  field,
  instant,
  int,
  listOf,
  nat,
  nullable,
  oneOf,
  paise,
  str,
  type Reader,
} from "./decode.ts";

export const READINESS_STATES = ["READY", "STALE", "MISSING", "BLOCKED"] as const;
export const SIDES = ["LONG", "SHORT"] as const;
export const SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"] as const;
export const LATCHES = ["NONE", "ENGAGED", "RELEASED"] as const;
export const FUNNEL_STAGES = [
  "NOT_EVALUATED",
  "MISSING_DATA",
  "REJECTED",
  "PASSED",
  "NOT_ALLOCATED",
] as const;
export const PLAN_STATUSES = ["ACTIVE", "EXPIRED", "INVALIDATED", "SUPERSEDED"] as const;
export const CALIBRATION = ["CALIBRATED", "UNCALIBRATED", "UNAVAILABLE"] as const;
export const EVIDENCE = ["SUFFICIENT", "INSUFFICIENT", "MISSING"] as const;
export const ALLOCATIONS = ["ALLOCATED", "DISPLACED", "NOT_ALLOCATED"] as const;
export const BAR_STATES = ["COMPLETE", "DEVELOPING"] as const;
export const GATE_RESULTS = ["PASS", "FAIL", "MISSING"] as const;
export const PROVENANCE_SOURCES = ["LIVE_CAPTURE", "REPLAY"] as const;

export type Side = (typeof SIDES)[number];
export type ReadinessState = (typeof READINESS_STATES)[number];
export type FunnelStage = (typeof FUNNEL_STAGES)[number];
export type PlanStatus = (typeof PLAN_STATUSES)[number];
export type CalibrationStatus = (typeof CALIBRATION)[number];
export type BarState = (typeof BAR_STATES)[number];
export type GateResult = (typeof GATE_RESULTS)[number];

// --- today ----------------------------------------------------------------------------------

export interface StrategyRow {
  readonly kind: "strategy";
  readonly version: string;
  readonly manifestFingerprint: string;
  readonly exchangeDate: string;
  readonly calendarVersion: string;
}
export interface ReadinessRow {
  readonly kind: "readiness";
  readonly item: string;
  readonly label: string;
  readonly state: ReadinessState;
  readonly scope: string;
  readonly reason: string | null;
  readonly evidenceRef: string;
  readonly checkedAt: string;
}
export interface Deadline {
  readonly name: string;
  readonly at: string;
}
export interface PhaseRow {
  readonly kind: "phase";
  readonly phase: string;
  readonly policy: string;
  readonly deadlines: readonly Deadline[];
}
export interface OpportunityRow {
  readonly kind: "opportunity";
  readonly listing: string;
  readonly side: Side | null;
  readonly status: "ACTIONABLE" | "NO_TRADE";
  readonly reason: string | null;
  readonly missingInputs: readonly string[];
  readonly planId: string | null;
}
export interface ExitApproach {
  readonly listing: string;
  readonly exitAt: string;
}
export interface ExposureRow {
  readonly kind: "exposure";
  readonly grossPaise: bigint;
  readonly reservedPaise: bigint;
  readonly unresolvedCount: number;
  readonly approachingExits: readonly ExitApproach[];
  readonly evidenceRef: string;
}
export interface IncidentRow {
  readonly kind: "incident";
  readonly incidentId: string;
  readonly severity: (typeof SEVERITIES)[number];
  readonly title: string;
  readonly owner: string | null;
  readonly onset: string;
  readonly acknowledged: boolean;
  readonly runbookRef: string;
  readonly safetyLatch: (typeof LATCHES)[number];
}
export type TodayRow =
  StrategyRow | ReadinessRow | PhaseRow | OpportunityRow | ExposureRow | IncidentRow;

const readDeadline: Reader<Deadline> = (value, path) => {
  const found = exact(value, path, ["name", "at"]);
  return { name: field(found, path, "name", str), at: field(found, path, "at", instant) };
};

const readExit: Reader<ExitApproach> = (value, path) => {
  const found = exact(value, path, ["listing", "exit_at"]);
  return {
    listing: field(found, path, "listing", str),
    exitAt: field(found, path, "exit_at", instant),
  };
};

/** Decode a `today` projection row; the `kind` field selects the shape. */
export function decodeTodayRow(value: unknown, path = "today"): TodayRow {
  const kind = field(
    { kind: (value as Record<string, unknown> | null)?.kind },
    path,
    "kind",
    oneOf(["strategy", "readiness", "phase", "opportunity", "exposure", "incident"] as const),
  );
  switch (kind) {
    case "strategy": {
      const f = exact(value, path, [
        "kind",
        "version",
        "manifest_fingerprint",
        "exchange_date",
        "calendar_version",
      ]);
      return {
        kind,
        version: field(f, path, "version", str),
        manifestFingerprint: field(f, path, "manifest_fingerprint", str),
        exchangeDate: field(f, path, "exchange_date", str),
        calendarVersion: field(f, path, "calendar_version", str),
      };
    }
    case "readiness": {
      const f = exact(value, path, [
        "kind",
        "item",
        "label",
        "state",
        "scope",
        "reason",
        "evidence_ref",
        "checked_at",
      ]);
      return {
        kind,
        item: field(f, path, "item", str),
        label: field(f, path, "label", str),
        state: field(f, path, "state", oneOf(READINESS_STATES)),
        scope: field(f, path, "scope", str),
        reason: field(f, path, "reason", nullable(str)),
        evidenceRef: field(f, path, "evidence_ref", str),
        checkedAt: field(f, path, "checked_at", instant),
      };
    }
    case "phase": {
      const f = exact(value, path, ["kind", "phase", "policy", "deadlines"]);
      return {
        kind,
        phase: field(f, path, "phase", str),
        policy: field(f, path, "policy", str),
        deadlines: field(f, path, "deadlines", listOf(readDeadline)),
      };
    }
    case "opportunity": {
      const f = exact(value, path, [
        "kind",
        "listing",
        "side",
        "status",
        "reason",
        "missing_inputs",
        "plan_id",
      ]);
      return {
        kind,
        listing: field(f, path, "listing", str),
        side: field(f, path, "side", nullable(oneOf(SIDES))),
        status: field(f, path, "status", oneOf(["ACTIONABLE", "NO_TRADE"] as const)),
        reason: field(f, path, "reason", nullable(str)),
        missingInputs: field(f, path, "missing_inputs", listOf(str)),
        planId: field(f, path, "plan_id", nullable(str)),
      };
    }
    case "exposure": {
      const f = exact(value, path, [
        "kind",
        "gross_paise",
        "reserved_paise",
        "unresolved_count",
        "approaching_exits",
        "evidence_ref",
      ]);
      return {
        kind,
        grossPaise: field(f, path, "gross_paise", paise),
        reservedPaise: field(f, path, "reserved_paise", paise),
        unresolvedCount: field(f, path, "unresolved_count", nat),
        approachingExits: field(f, path, "approaching_exits", listOf(readExit)),
        evidenceRef: field(f, path, "evidence_ref", str),
      };
    }
    case "incident": {
      const f = exact(value, path, [
        "kind",
        "incident_id",
        "severity",
        "title",
        "owner",
        "onset",
        "acknowledged",
        "runbook_ref",
        "safety_latch",
      ]);
      return {
        kind,
        incidentId: field(f, path, "incident_id", str),
        severity: field(f, path, "severity", oneOf(SEVERITIES)),
        title: field(f, path, "title", str),
        owner: field(f, path, "owner", nullable(str)),
        onset: field(f, path, "onset", instant),
        acknowledged: field(f, path, "acknowledged", bool),
        runbookRef: field(f, path, "runbook_ref", str),
        safetyLatch: field(f, path, "safety_latch", oneOf(LATCHES)),
      };
    }
  }
}

// --- scanner --------------------------------------------------------------------------------

export interface FunnelRow {
  readonly stage: FunnelStage;
  readonly count: number;
  readonly reasons: readonly { readonly reason: string; readonly count: number }[];
}

/** Decode a `scanner_funnel` row. */
export function decodeFunnelRow(value: unknown, path = "funnel"): FunnelRow {
  const f = exact(value, path, ["stage", "count", "reasons"]);
  return {
    stage: field(f, path, "stage", oneOf(FUNNEL_STAGES)),
    count: field(f, path, "count", nat),
    reasons: field(
      f,
      path,
      "reasons",
      listOf((item, where) => {
        const g = exact(item, where, ["reason", "count"]);
        return { reason: field(g, where, "reason", str), count: field(g, where, "count", nat) };
      }),
    ),
  };
}

export interface ScanRow {
  readonly listing: string;
  readonly sector: string;
  readonly side: Side | null;
  readonly stage: FunnelStage;
  readonly reason: string | null;
  readonly turnoverPaise: bigint | null;
  readonly participationPpm: number | null;
  readonly gapBp: number | null;
  readonly catalyst: string | null;
  readonly definitionVersion: string;
}

/** Decode a `scanner` row; absent metrics stay `null` (never zero). */
export function decodeScanRow(value: unknown, path = "scan"): ScanRow {
  const f = exact(value, path, [
    "listing",
    "sector",
    "side",
    "stage",
    "reason",
    "turnover_paise",
    "participation_ppm",
    "gap_bp",
    "catalyst",
    "definition_version",
  ]);
  return {
    listing: field(f, path, "listing", str),
    sector: field(f, path, "sector", str),
    side: field(f, path, "side", nullable(oneOf(SIDES))),
    stage: field(f, path, "stage", oneOf(FUNNEL_STAGES)),
    reason: field(f, path, "reason", nullable(str)),
    turnoverPaise: field(f, path, "turnover_paise", nullable(paise)),
    participationPpm: field(f, path, "participation_ppm", nullable(nat)),
    gapBp: field(f, path, "gap_bp", nullable(int)),
    catalyst: field(f, path, "catalyst", nullable(str)),
    definitionVersion: field(f, path, "definition_version", str),
  };
}

// --- board ----------------------------------------------------------------------------------

export interface TradePlan {
  readonly triggerPaise: bigint;
  readonly entryLowPaise: bigint;
  readonly entryHighPaise: bigint;
  readonly stopPaise: bigint;
  readonly quantity: number;
  readonly notionalPaise: bigint;
  readonly plannedRiskPaise: bigint;
  readonly stressedRiskPaise: bigint;
  readonly costsPaise: bigint;
  readonly exitPolicy: string;
}
export interface CatalystRef {
  readonly label: string;
  readonly sourceUrl: string;
  readonly revision: number;
}
export interface BoardRow {
  readonly planId: string;
  readonly decisionId: string;
  readonly listing: string;
  readonly side: Side;
  readonly setup: string;
  readonly rank: number;
  readonly evidenceStatus: (typeof EVIDENCE)[number];
  readonly calibrationStatus: CalibrationStatus;
  /** A decimal string of scale `PROBABILITY_SCALE`, shown verbatim; only ever calibrated. */
  readonly probability: string | null;
  readonly probabilityNote: string | null;
  readonly catalyst: CatalystRef | null;
  readonly decisionAt: string;
  readonly expiresAt: string;
  readonly status: PlanStatus;
  readonly supersededBy: string | null;
  readonly allocation: (typeof ALLOCATIONS)[number];
  readonly explanation: string;
  readonly plan: TradePlan | null;
}

const readPlan: Reader<TradePlan> = (value, path) => {
  const f = exact(value, path, [
    "trigger_paise",
    "entry_low_paise",
    "entry_high_paise",
    "stop_paise",
    "quantity",
    "notional_paise",
    "planned_risk_paise",
    "stressed_risk_paise",
    "costs_paise",
    "exit_policy",
  ]);
  return {
    triggerPaise: field(f, path, "trigger_paise", paise),
    entryLowPaise: field(f, path, "entry_low_paise", paise),
    entryHighPaise: field(f, path, "entry_high_paise", paise),
    stopPaise: field(f, path, "stop_paise", paise),
    quantity: field(f, path, "quantity", nat),
    notionalPaise: field(f, path, "notional_paise", paise),
    plannedRiskPaise: field(f, path, "planned_risk_paise", paise),
    stressedRiskPaise: field(f, path, "stressed_risk_paise", paise),
    costsPaise: field(f, path, "costs_paise", paise),
    exitPolicy: field(f, path, "exit_policy", str),
  };
};

const readCatalystRef: Reader<CatalystRef> = (value, path) => {
  const f = exact(value, path, ["label", "source_url", "revision"]);
  return {
    label: field(f, path, "label", str),
    sourceUrl: field(f, path, "source_url", str),
    revision: field(f, path, "revision", nat),
  };
};

/** Decode a `board` row; a probability is `null` unless the server supplies one. */
export function decodeBoardRow(value: unknown, path = "plan"): BoardRow {
  const f = exact(value, path, [
    "plan_id",
    "decision_id",
    "listing",
    "side",
    "setup",
    "rank",
    "evidence_status",
    "calibration_status",
    "probability",
    "probability_note",
    "catalyst",
    "decision_at",
    "expires_at",
    "status",
    "superseded_by",
    "allocation",
    "explanation",
    "plan",
  ]);
  return {
    planId: field(f, path, "plan_id", str),
    decisionId: field(f, path, "decision_id", str),
    listing: field(f, path, "listing", str),
    side: field(f, path, "side", oneOf(SIDES)),
    setup: field(f, path, "setup", str),
    rank: field(f, path, "rank", nat),
    evidenceStatus: field(f, path, "evidence_status", oneOf(EVIDENCE)),
    calibrationStatus: field(f, path, "calibration_status", oneOf(CALIBRATION)),
    probability: field(f, path, "probability", nullable(decimal(PROBABILITY_SCALE))),
    probabilityNote: field(f, path, "probability_note", nullable(str)),
    catalyst: field(f, path, "catalyst", nullable(readCatalystRef)),
    decisionAt: field(f, path, "decision_at", instant),
    expiresAt: field(f, path, "expires_at", instant),
    status: field(f, path, "status", oneOf(PLAN_STATUSES)),
    supersededBy: field(f, path, "superseded_by", nullable(str)),
    allocation: field(f, path, "allocation", oneOf(ALLOCATIONS)),
    explanation: field(f, path, "explanation", str),
    plan: field(f, path, "plan", nullable(readPlan)),
  };
}

// --- detail ---------------------------------------------------------------------------------

export interface Bar {
  readonly start: string;
  readonly end: string;
  readonly openPaise: bigint;
  readonly highPaise: bigint;
  readonly lowPaise: bigint;
  readonly closePaise: bigint;
  readonly volume: number;
  readonly state: BarState;
  readonly gap: boolean;
  readonly stale: boolean;
}
export interface Levels {
  readonly openingRangeHighPaise: bigint | null;
  readonly openingRangeLowPaise: bigint | null;
  readonly entryPaise: bigint | null;
  readonly stopPaise: bigint | null;
  readonly exitPaise: bigint | null;
}
export interface Gate {
  readonly name: string;
  readonly role: string;
  readonly scope: string;
  readonly disposition: string;
  readonly result: GateResult;
  readonly observed: string | null;
  readonly observedAt: string | null;
  readonly recordRef: string | null;
}
export interface CatalystRevision {
  readonly revision: number;
  readonly receivedAt: string;
  readonly classifiedAt: string;
  readonly classificationVersion: string;
}
export interface CatalystEvidence {
  readonly label: string;
  readonly sourceUrl: string;
  readonly excerpt: string;
  readonly publishedAt: string;
  readonly receivedAt: string;
  readonly classifiedAt: string;
  readonly classificationVersion: string;
  readonly revision: number;
  readonly revisions: readonly CatalystRevision[];
}
export interface DetailRow {
  readonly decisionId: string;
  readonly listing: string;
  readonly decisionAt: string;
  readonly sessionDate: string;
  readonly provenance: {
    readonly source: (typeof PROVENANCE_SOURCES)[number];
    readonly engineVersion: string;
    readonly barSourceVersion: string;
  };
  readonly references: {
    readonly assessmentId: string | null;
    readonly featureSnapshotId: string | null;
    readonly manifestFingerprint: string;
    readonly snapshotId: string | null;
    readonly universeId: string;
  };
  readonly bars: readonly Bar[];
  readonly vwapPaise: readonly (bigint | null)[];
  readonly levels: Levels;
  readonly uncertainExecution: boolean;
  readonly gates: readonly Gate[];
  readonly catalyst: CatalystEvidence | null;
}

const readBar: Reader<Bar> = (value, path) => {
  const f = exact(value, path, [
    "start",
    "end",
    "open_paise",
    "high_paise",
    "low_paise",
    "close_paise",
    "volume",
    "state",
    "gap",
    "stale",
  ]);
  return {
    start: field(f, path, "start", instant),
    end: field(f, path, "end", instant),
    openPaise: field(f, path, "open_paise", paise),
    highPaise: field(f, path, "high_paise", paise),
    lowPaise: field(f, path, "low_paise", paise),
    closePaise: field(f, path, "close_paise", paise),
    volume: field(f, path, "volume", nat),
    state: field(f, path, "state", oneOf(BAR_STATES)),
    gap: field(f, path, "gap", bool),
    stale: field(f, path, "stale", bool),
  };
};

const readGate: Reader<Gate> = (value, path) => {
  const f = exact(value, path, [
    "name",
    "role",
    "scope",
    "disposition",
    "result",
    "observed",
    "observed_at",
    "record_ref",
  ]);
  return {
    name: field(f, path, "name", str),
    role: field(f, path, "role", str),
    scope: field(f, path, "scope", str),
    disposition: field(f, path, "disposition", str),
    result: field(f, path, "result", oneOf(GATE_RESULTS)),
    observed: field(f, path, "observed", nullable(str)),
    observedAt: field(f, path, "observed_at", nullable(instant)),
    recordRef: field(f, path, "record_ref", nullable(str)),
  };
};

const readRevision: Reader<CatalystRevision> = (value, path) => {
  const f = exact(value, path, [
    "revision",
    "received_at",
    "classified_at",
    "classification_version",
  ]);
  return {
    revision: field(f, path, "revision", nat),
    receivedAt: field(f, path, "received_at", instant),
    classifiedAt: field(f, path, "classified_at", instant),
    classificationVersion: field(f, path, "classification_version", str),
  };
};

const readEvidence: Reader<CatalystEvidence> = (value, path) => {
  const f = exact(value, path, [
    "label",
    "source_url",
    "excerpt",
    "published_at",
    "received_at",
    "classified_at",
    "classification_version",
    "revision",
    "revisions",
  ]);
  return {
    label: field(f, path, "label", str),
    sourceUrl: field(f, path, "source_url", str),
    excerpt: field(f, path, "excerpt", str),
    publishedAt: field(f, path, "published_at", instant),
    receivedAt: field(f, path, "received_at", instant),
    classifiedAt: field(f, path, "classified_at", instant),
    classificationVersion: field(f, path, "classification_version", str),
    revision: field(f, path, "revision", nat),
    revisions: field(f, path, "revisions", listOf(readRevision)),
  };
};

/** Decode the `detail` row; levels and VWAP are the server's values, never recomputed. */
export function decodeDetailRow(value: unknown, path = "detail"): DetailRow {
  const f = exact(value, path, [
    "decision_id",
    "listing",
    "decision_at",
    "session_date",
    "provenance",
    "references",
    "bars",
    "vwap_paise",
    "levels",
    "uncertain_execution",
    "gates",
    "catalyst",
  ]);
  const detail: DetailRow = {
    decisionId: field(f, path, "decision_id", str),
    listing: field(f, path, "listing", str),
    decisionAt: field(f, path, "decision_at", instant),
    sessionDate: field(f, path, "session_date", str),
    provenance: field(f, path, "provenance", (inner, where) => {
      const g = exact(inner, where, ["source", "engine_version", "bar_source_version"]);
      return {
        source: field(g, where, "source", oneOf(PROVENANCE_SOURCES)),
        engineVersion: field(g, where, "engine_version", str),
        barSourceVersion: field(g, where, "bar_source_version", str),
      };
    }),
    references: field(f, path, "references", (inner, where) => {
      const g = exact(inner, where, [
        "assessment_id",
        "feature_snapshot_id",
        "manifest_fingerprint",
        "snapshot_id",
        "universe_id",
      ]);
      return {
        assessmentId: field(g, where, "assessment_id", nullable(str)),
        featureSnapshotId: field(g, where, "feature_snapshot_id", nullable(str)),
        manifestFingerprint: field(g, where, "manifest_fingerprint", str),
        snapshotId: field(g, where, "snapshot_id", nullable(str)),
        universeId: field(g, where, "universe_id", str),
      };
    }),
    bars: field(f, path, "bars", listOf(readBar)),
    vwapPaise: field(f, path, "vwap_paise", listOf(nullable(paise))),
    levels: field(f, path, "levels", (inner, where) => {
      const g = exact(inner, where, [
        "opening_range_high_paise",
        "opening_range_low_paise",
        "entry_paise",
        "stop_paise",
        "exit_paise",
      ]);
      return {
        openingRangeHighPaise: field(g, where, "opening_range_high_paise", nullable(paise)),
        openingRangeLowPaise: field(g, where, "opening_range_low_paise", nullable(paise)),
        entryPaise: field(g, where, "entry_paise", nullable(paise)),
        stopPaise: field(g, where, "stop_paise", nullable(paise)),
        exitPaise: field(g, where, "exit_paise", nullable(paise)),
      };
    }),
    uncertainExecution: field(f, path, "uncertain_execution", bool),
    gates: field(f, path, "gates", listOf(readGate)),
    catalyst: field(f, path, "catalyst", nullable(readEvidence)),
  };
  if (detail.vwapPaise.length !== detail.bars.length) {
    throw new ContractViolation(`${path}.vwap_paise`, "one VWAP value per bar");
  }
  return detail;
}

// --- accounts -------------------------------------------------------------------------------

export interface AccountRow {
  readonly accountId: string;
  readonly label: string;
  readonly mode: string;
}

/** Decode an `accounts` row. */
export function decodeAccountRow(value: unknown, path = "account"): AccountRow {
  const f = exact(value, path, ["account_id", "label", "mode"]);
  return {
    accountId: field(f, path, "account_id", str),
    label: field(f, path, "label", str),
    mode: field(f, path, "mode", str),
  };
}
